import { useCallback, useEffect, useRef, useState } from 'react';

import { useStores } from '@/components/hooks';
import { BASEURL, ENDPOINTS } from '@/api';
import { captureErrorForSentry } from '@/components/utils';

const domainCache = new Map<string, boolean>();

interface UsePublicDomainCheckResult {
    isPublicDomain: boolean | null;
    isLoading: boolean;
}

const usePublicDomainCheck = (email: string): UsePublicDomainCheckResult => {
    const { authSettingsStore: { API } } = useStores();
    const [isPublicDomain, setIsPublicDomain] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const domain = email?.split('@')[1]?.toLowerCase();

    useEffect(() => {
        if (!domain) return;

        if (domainCache.has(domain)) {
            setIsPublicDomain(domainCache.get(domain));
            return;
        }

        const checkDomain = async (): Promise<void> => {
            setIsLoading(true);
            try {
                const response = await API.post(
                    BASEURL.backend(),
                    ENDPOINTS.isPublicDomain(),
                    { body: { domain } },
                );
                const result = !!response?.isDomainPublic;
                domainCache.set(domain, result);
                if (mountedRef.current) {
                    setIsPublicDomain(result);
                }
            } catch (error) {
                captureErrorForSentry(error, 'usePublicDomainCheck');
                if (mountedRef.current) {
                    setIsPublicDomain(false);
                }
            } finally {
                if (mountedRef.current) {
                    setIsLoading(false);
                }
            }
        };

        checkDomain();
    }, [domain]);

    return { isPublicDomain, isLoading };
};

export default usePublicDomainCheck;
